'use client'

import { mapCartToElevarViewCartData } from '@repo/elevar/mapper'
import { NavigationQueryResult } from '@repo/sanity/types'
import { useProducts } from '@repo/shop/hooks'
import { Button } from '@repo/ui/components/button'
import { Close } from '@repo/ui/components/close'
import Price from '@repo/ui/components/price'
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@repo/ui/components/sheet'
import { cn } from '@repo/ui/lib/utils'
import Link from 'next/link'
import { FunctionComponent, useEffect, useRef, useState } from 'react'
import { createCartAndSetCookie } from '../../../actions/cart.actions'
import { useCart } from '../../../providers/CartProvider'
import { useNavigation } from '../../../providers/NavigationProvider'
import { useRecentlyViewed } from '../../../providers/RecentlyViewedProvider'
import { SHEET, useSheet } from '../../../providers/SheetProvider'
import { useTelemetry } from '../../../providers/TelemetryProvider'
import { PageDto } from '../../../types/page.types'
import CartProducts from '../../cart/CartProducts'
import CartAccordion from './CartAccordion'
import CartItems from './CartItems'

interface Props {
  pageDto: PageDto
  navigation: NavigationQueryResult
}

const Description: FunctionComponent<Props> = ({ pageDto }) => {
  const { cart } = useCart()
  const qty = cart?.totalQuantity || 0

  if (qty > 0) {
    const copy =
      qty === 1 ? pageDto.dictionary?.cart?.yourbagcontainsxitem || 'Your bag contains %1 item.' : pageDto.dictionary?.cart?.yourbagcontainsxitems || 'Your bag contains %1 items.'

    return <>{copy.replace('%1', qty.toString())}</>
  }

  return <span className='inline-block min-h-[2rem]'>{pageDto.dictionary?.cart?.yourbagisempty || 'Your bag is empty.'}</span>
}

const CartSheet: FunctionComponent<Props> = ({ pageDto, navigation }) => {
  const { cart } = useCart()
  const { shouldUseInverse } = useNavigation()
  const { trackElevarViewCartEvent } = useTelemetry()
  const { openSheet, isSheetOpen, closeSheet } = useSheet()
  const [isClient, setIsClient] = useState(false)
  const quantityRef = useRef(cart?.totalQuantity)
  const { recentlyViewedProductUrlSlugs, removeRecentlyViewed } = useRecentlyViewed()
  const qty = cart?.totalQuantity || 0

  useEffect(() => {
    setIsClient(true)
  }, [])

  const { data: recentlyViewedProducts } = useProducts({
    locale: pageDto.params.locale,
    options: { revalidateOnFocus: false },
    // We only show recently viewed products when there are no wishlist items, so conditionally fetch.
    urlSlugs: qty === 0 && isSheetOpen(SHEET.CART) ? recentlyViewedProductUrlSlugs : [],
  })

  // Create cart if it doesn't exist yet.
  useEffect(() => {
    if (!cart?.id) {
      createCartAndSetCookie(pageDto.shopifyInstance)
    }
  }, [cart, pageDto.shopifyInstance])

  // Open cart when quantity changes.
  useEffect(() => {
    if (qty !== quantityRef.current && qty > 0) {
      if (!isSheetOpen(SHEET.CART)) {
        openSheet(SHEET.CART)
      }

      quantityRef.current = qty
    }
  }, [isSheetOpen, openSheet, qty, quantityRef])

  // Track open event.
  useEffect(() => {
    if (isSheetOpen(SHEET.CART) && cart) {
      trackElevarViewCartEvent(mapCartToElevarViewCartData(cart, pageDto.params.locale))
    }
  }, [cart, pageDto.params.locale, isSheetOpen, trackElevarViewCartEvent])

  return (
    <>
      <li>
        <Button variant={shouldUseInverse(navigation) ? 'navigationInverse' : 'navigationDefault'} onClick={() => openSheet(SHEET.CART)}>
          {pageDto.dictionary?.cart?.bag || 'Bag'}
          <span
            inert={!(isClient && qty > 0)}
            aria-label={isClient && qty > 0 ? qty.toString() : undefined}
            className={cn(
              'bg-miista-green-500 absolute right-1 top-1 block size-1 rounded-full opacity-0 transition-opacity duration-300 ease-out',
              isClient && qty > 0 && 'opacity-100',
            )}
          />
        </Button>
      </li>
      <Sheet open={isSheetOpen(SHEET.CART)} onOpenChange={(open) => !open && closeSheet()}>
        <SheetContent side='right'>
          <SheetHeader>
            <SheetTitle>{pageDto.dictionary?.cart?.bag || 'Bag'}</SheetTitle>
          </SheetHeader>
          <SheetDescription>
            <Description pageDto={pageDto} navigation={navigation} />
          </SheetDescription>
          <div className='flex h-full flex-col overflow-y-auto'>
            <CartItems pageDto={pageDto} closeCart={closeSheet} />
            {qty === 0 && recentlyViewedProducts && (recentlyViewedProducts || [])?.length > 0 && (
              <div className='border-border border-t border-solid'>
                <h3 className='flex items-center gap-1 px-3 pb-1 pt-3 uppercase'>
                  {pageDto.dictionary?.navigation?.recentlyviewed || 'Recently Viewed'}
                  <button className='relative -top-px' type='button' onClick={removeRecentlyViewed} aria-label={pageDto.dictionary?.navigation?.clear || 'Clear'}>
                    <Close ariaLabel={undefined} />
                  </button>
                </h3>
                <CartProducts pageDto={pageDto} products={recentlyViewedProducts} onClick={closeSheet} />
              </div>
            )}
            {qty > 0 && navigation?.cartSettings?.accordion && <CartAccordion navigation={navigation} locale={pageDto.params.locale} />}
          </div>
          {qty > 0 && cart?.checkoutUrl && (
            <SheetFooter className='border-border items-start justify-between border-t border-solid pt-3'>
              <div className='uppercase'>
                {pageDto.dictionary?.cart?.subtotal || 'Subtotal:'} <Price amount={parseFloat(cart.cost.totalAmount.amount)} currencyCode={cart.cost.totalAmount.currencyCode} />
              </div>
              <div className='space-y-3 text-right'>
                <Button variant='defaultStatic' asChild={true} onClick={closeSheet}>
                  <Link href={cart.checkoutUrl}>{pageDto.dictionary?.cart?.checkout || 'Checkout'}</Link>
                </Button>
                {navigation?.cartSettings?.checkoutButtonInfo && <p className='text-muted'>{navigation?.cartSettings?.checkoutButtonInfo}</p>}
              </div>
            </SheetFooter>
          )}
        </SheetContent>
      </Sheet>
    </>
  )
}

export default CartSheet
